import CloseIcon from "@mui/icons-material/Close";
import CommentIcon from "@mui/icons-material/Comment";
import Badge from "@mui/material/Badge";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Portal from "@mui/material/Portal";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import Guid from "_shared/guids/guid";
import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
import {
  ActionsContainer,
  Content,
  Header,
  SecondaryActions,
  TabContent,
  TabsContainer,
} from "work/_views/work-dialog-styled-components";
import { getProposalById } from "work/proposals/_store/proposals-redux-slice";
import ArchivedBadge from "work/proposals/_views/archive/archived-badge";
import ConflictsArchivedTab from "work/proposals/_views/archive/dialog-tabs/conflicts-archived-tab";
import DetailsArchivedTab from "work/proposals/_views/archive/dialog-tabs/details-archived-tab";
import DiscountArchivedTab from "work/proposals/_views/archive/dialog-tabs/discount-archived-tab";
import FeeScheduleArchivedTab from "work/proposals/_views/archive/dialog-tabs/fee-schedule-archived-tab";
import PoliciesArchivedTab from "work/proposals/_views/archive/dialog-tabs/policies-archived-tab";
import TeamTabArchived from "work/proposals/_views/archive/dialog-tabs/team-archived-tab";
import { getCommentThreadsByProposal } from "work/proposals/comments/threads/_store/comment-thread-redux-slice";
import CommentsReadonly from "work/proposals/comments/_view/comments-readonly";
import ProposalField, {
  HumanReadableProposalFieldName,
  ProposalFieldCategory,
  ProposalFieldCategoryType,
} from "work/proposals/proposal-fields/proposal-field";
import Proposal from "work/proposals/proposal";

const DialogTab = styled(Tab)(() => ({
  "&.MuiTab-root": {
    overflow: "visible",
  },
}));
const MessageButtons = styled("section")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  marginLeft: theme.spacing(2),
  width: "100%",
}));
const ButtonContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
}));
const MessageButton = styled(IconButton)(() => ({
  paddingBottom: 0,
}));
const MessageButtonLabel = styled(Typography)(() => ({
  fontSize: "0.6em",
}));
const SidePanel = styled(Drawer, { shouldForwardProp: (prop) => prop !== "closeAfterTransition" })(({ theme }) => ({
  position: "fixed",
  zIndex: theme.zIndex.modal + 1,
}));
const SidePanelContainer = styled("div")(() => ({
  flex: 1,
  height: "100%",
}));
const SidePanelContent = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  padding: theme.spacing(2),
  width: "400px",
}));
const TitleBar = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  justifyContent: "space-between",
  flexDirection: "row",
  paddingBottom: theme.spacing(1),
}));

function adjustDialogPosition(panelOpen: boolean) {
  for (const dialog of document.getElementsByClassName("MuiDialog-root")) {
    dialog.setAttribute("style", `padding-right: ${panelOpen ? "0px" : "400px"}; transition: padding-right 225ms;`);
  }
}

type ProposalArchiveDialogProps = {
  proposalId?: Guid;
  navigate: (url: string) => void;
};

export default function ArchivedProposalDialog(props: Readonly<ProposalArchiveDialogProps>) {
  const { proposalId } = props;
  const session = useSession();

  const [activeTab, setActiveTab] = React.useState<ProposalFieldCategoryType>(ProposalFieldCategory.Details);
  const [currentCommentField, setCurrentCommentField] = React.useState<ProposalField | undefined>();
  const [sidePanelTitle, setSidePanelTitle] = React.useState<string | undefined>();
  const [isSidePanelOpen, setIsSidePanelOpen] = React.useState<boolean>(false);

  const currentUser = session.user!;

  const proposalJson = useSelector(getProposalById(proposalId));
  const proposal = useMemo(() => {
    if (!proposalJson) return null;
    return Proposal.fromJSON({ json: proposalJson, currentUser }) ?? null;
  }, [proposalJson, currentUser]);
  const commentThreads = useSelector(getCommentThreadsByProposal(proposal, proposalId));

  function handleTabChange(_event: React.SyntheticEvent | null, newTab: ProposalFieldCategoryType) {
    setActiveTab(newTab);
  }

  function handleCloseCommentsPanel() {
    setIsSidePanelOpen(false);
    adjustDialogPosition(true);
  }

  async function handleOpenCommentsPanel(field: ProposalField, sidePanelTitle?: string) {
    // Don't open comments panel if there are no comments
    const threads = commentThreads?.some((thread) => thread.field.isEqualTo(field));
    if (!threads) return;

    setCurrentCommentField(field);
    setSidePanelTitle(sidePanelTitle);
    setIsSidePanelOpen(true);
    adjustDialogPosition(false);
  }

  function getSidePanelTitle(): string {
    if (sidePanelTitle) return `${sidePanelTitle} Comments`;
    return `${HumanReadableProposalFieldName[currentCommentField?.name ?? ProposalField.General.name]} Comments`;
  }

  function renderComments() {
    if (proposal?.id && commentThreads) {
      return (
        <CommentsReadonly
          proposal={proposal}
          field={currentCommentField ?? ProposalField.General}
          commentThreads={commentThreads}
        />
      );
    }
  }

  return (
    <>
      <Header>
        <TabsContainer
          variant="scrollable"
          scrollButtons="auto"
          indicatorColor="primary"
          textColor="primary"
          value={activeTab}
          onChange={handleTabChange}
        >
          <DialogTab
            value={ProposalFieldCategory.Details}
            label={<ArchivedBadge category={ProposalFieldCategory.Details} />}
          />
          <DialogTab
            value={ProposalFieldCategory.Team}
            label={<ArchivedBadge category={ProposalFieldCategory.Team} />}
          />
          <DialogTab
            value={ProposalFieldCategory.FeeSchedule}
            label={<ArchivedBadge category={ProposalFieldCategory.FeeSchedule} />}
          />
          <DialogTab
            value={ProposalFieldCategory.Conflicts}
            label={<ArchivedBadge category={ProposalFieldCategory.Conflicts} />}
          />
          <DialogTab
            value={ProposalFieldCategory.Policies}
            label={<ArchivedBadge category={ProposalFieldCategory.Policies} />}
          />
          <DialogTab
            value={ProposalFieldCategory.Discount}
            label={<ArchivedBadge category={ProposalFieldCategory.Discount} />}
          />
        </TabsContainer>
      </Header>
      <Content>
        <TabContent>
          {proposal && (
            <>
              <DetailsArchivedTab
                commentThreads={commentThreads ?? []}
                proposal={proposal}
                activeTab={activeTab}
                onCommentsClicked={handleOpenCommentsPanel}
              />
              <TeamTabArchived
                entityId={session.user!.legalEntityId}
                proposal={proposal}
                activeTab={activeTab}
                commentThreads={commentThreads ?? []}
                onCommentsClicked={handleOpenCommentsPanel}
              />
              <FeeScheduleArchivedTab
                proposal={proposal}
                activeTab={activeTab}
                commentThreads={commentThreads}
                onCommentsClicked={handleOpenCommentsPanel}
              />
              <ConflictsArchivedTab
                proposal={proposal}
                activeTab={activeTab}
                commentThreads={commentThreads}
                onCommentsClicked={handleOpenCommentsPanel}
              />
              <PoliciesArchivedTab
                proposal={proposal}
                activeTab={activeTab}
                commentThreads={commentThreads}
                onCommentsClicked={handleOpenCommentsPanel}
              />
              <DiscountArchivedTab
                proposal={proposal}
                activeTab={activeTab}
                commentThreads={commentThreads}
                onCommentsClicked={handleOpenCommentsPanel}
              />
            </>
          )}
        </TabContent>
      </Content>
      <ActionsContainer>
        <SecondaryActions />
        <MessageButtons>
          <ButtonContainer sx={{ flex: 1 }}>{/* Placeholder for additional buttons */}</ButtonContainer>
          <ButtonContainer>
            <MessageButton
              size="medium"
              color="primary"
              disabled={!proposal?.id || !proposal || !commentThreads}
              onClick={() => handleOpenCommentsPanel(ProposalField.General)}
            >
              <Badge
                variant="standard"
                color="secondary"
                overlap="rectangular"
                invisible={!commentThreads?.some((thread) => thread.field.isEqualTo(ProposalField.General))}
              >
                <CommentIcon fontSize="medium" />
              </Badge>
            </MessageButton>
            <MessageButtonLabel
              variant="button"
              color={!proposal?.id || !proposal || !commentThreads ? "darkgray" : "primary"}
            >
              Comments
            </MessageButtonLabel>
          </ButtonContainer>
        </MessageButtons>
        <Portal>
          <SidePanel open={isSidePanelOpen} anchor="right" variant="persistent" closeAfterTransition={false}>
            <SidePanelContainer>
              <SidePanelContent>
                <TitleBar>
                  <Typography variant="h5">{getSidePanelTitle()}</Typography>
                  <IconButton onClick={handleCloseCommentsPanel}>
                    <CloseIcon />
                  </IconButton>
                </TitleBar>
                {renderComments()}
              </SidePanelContent>
            </SidePanelContainer>
          </SidePanel>
        </Portal>
      </ActionsContainer>
    </>
  );
}
